import { useState, useMemo, useCallback } from "react"
import { email_accounts_path, bulk_email_accounts_path } from "~/lib/routes"
import { getCsrfToken } from "~/lib/utils"
import type { EmailAccount, EmailAccountsData } from "~/react/components/email_accounts/email_accounts_filters"
import { isPendingAccount } from "../utils"

type BulkEditFlags = {
  accountIds: number[]
  canEditSenderName: boolean
  canEditSignature: boolean
  canEditManagedFields: boolean
  count: number
}

type BulkEditData = {
  daily_limit: number
  default_account: boolean
  sender_name: string
  signature: string
  managedMailbox: {
    first_name: string
    last_name: string
  }
}

const DEFAULT_BULK_EDIT_DATA: BulkEditData = {
  daily_limit: 0,
  default_account: false,
  sender_name: "",
  signature: "",
  managedMailbox: { first_name: "", last_name: "" },
}

const DEFAULT_BULK_EDIT_FLAGS: BulkEditFlags = {
  accountIds: [],
  canEditSenderName: false,
  canEditSignature: false,
  canEditManagedFields: false,
  count: 0,
}

type UseBulkEditArgs = {
  allItems: EmailAccount[]
  selectedIds: Array<string | number>
  clearSelection: () => void
  setData: React.Dispatch<React.SetStateAction<EmailAccountsData | null>>
}

export function useBulkEdit({ allItems, selectedIds, clearSelection, setData }: UseBulkEditArgs) {
  const [bulkEditOpen, setBulkEditOpen] = useState(false)
  const [bulkEditLoading, setBulkEditLoading] = useState(false)
  const [bulkEditError, setBulkEditError] = useState<string | null>(null)
  const [bulkEditFlags, setBulkEditFlags] = useState<BulkEditFlags>(DEFAULT_BULK_EDIT_FLAGS)
  const [bulkEditData, setBulkEditData] = useState<BulkEditData>(DEFAULT_BULK_EDIT_DATA)

  const getNumericIds = useCallback((ids: Array<string | number>): number[] => {
    return ids.filter((id): id is number => typeof id === "number")
  }, [])

  const bulkEditAccount = useMemo<EmailAccount>(
    () => ({
      id: -1,
      email: i18n.t("js.email_accounts_center.bulk.edit_title", { count: bulkEditFlags.count }),
      status: "active",
      sent: 0,
      limit: 0,
      scheduled: 0,
      health: null,
      protection: "",
      owner_id: 0,
      owner_name: "",
      provider: "bulk",
      managed: bulkEditFlags.canEditManagedFields,
      _bulkEdit: {
        accountIds: bulkEditFlags.accountIds,
        canEditSenderName: bulkEditFlags.canEditSenderName,
        canEditSignature: bulkEditFlags.canEditSignature,
        canEditManagedFields: bulkEditFlags.canEditManagedFields,
      },
    }),
    [bulkEditFlags],
  )

  const handleEdit = useCallback(() => {
    if (selectedIds.length === 0) return

    const selectedAccounts = allItems.filter((account) => selectedIds.includes(account.id))
    if (selectedAccounts.length === 0) return

    const allAreManaged = selectedAccounts.every((acc) => acc.managed)
    const allCanEditSenderName = selectedAccounts.every(
      (acc) => acc.provider !== "outlook" && acc.provider !== "smtp_imap",
    )
    const allCanEditSignature = selectedAccounts.every((acc) => acc.managed || acc.provider === "smtp_imap")

    const numericIds = getNumericIds(selectedIds)
    if (numericIds.length === 0) return

    setBulkEditFlags({
      accountIds: numericIds,
      canEditSenderName: allCanEditSenderName,
      canEditSignature: allCanEditSignature,
      canEditManagedFields: allAreManaged,
      count: numericIds.length,
    })
    setBulkEditData(DEFAULT_BULK_EDIT_DATA)
    setBulkEditError(null)
    setBulkEditOpen(true)
  }, [selectedIds, allItems, getNumericIds])

  const handleBulkEditSubmit = useCallback(async () => {
    if (bulkEditFlags.accountIds.length === 0) return

    setBulkEditLoading(true)
    setBulkEditError(null)

    try {
      const emailAccountPayload: Record<string, unknown> = {}

      if (bulkEditFlags.canEditSenderName && bulkEditData.sender_name.trim()) {
        emailAccountPayload.display_name = bulkEditData.sender_name.trim()
      }

      if (bulkEditData.daily_limit > 0) {
        emailAccountPayload.daily_messages_limit = bulkEditData.daily_limit
      }

      if (bulkEditFlags.canEditSignature && (bulkEditData.signature || "").trim()) {
        emailAccountPayload.signature = bulkEditData.signature.trim()
      }

      const managedMailboxPayload: Record<string, string> = {}
      if (bulkEditFlags.canEditManagedFields) {
        if (bulkEditData.managedMailbox.first_name.trim()) {
          managedMailboxPayload.first_name = bulkEditData.managedMailbox.first_name.trim()
        }
        if (bulkEditData.managedMailbox.last_name.trim()) {
          managedMailboxPayload.last_name = bulkEditData.managedMailbox.last_name.trim()
        }
      }

      const body: Record<string, unknown> = {
        ids: bulkEditFlags.accountIds,
        email_account: emailAccountPayload,
      }

      if (bulkEditFlags.canEditManagedFields && Object.keys(managedMailboxPayload).length > 0) {
        body.managed_mailbox = managedMailboxPayload
      }

      const response = await fetch(bulk_email_accounts_path(), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify(body),
      })

      if (response.ok) {
        const result = await response.json()
        const updatedCount = result.updated_count || bulkEditFlags.accountIds.length

        setBulkEditOpen(false)
        clearSelection()
        setBulkEditData(DEFAULT_BULK_EDIT_DATA)

        App.displayFlash("success", i18n.t("js.email_accounts_center.bulk.edit_success", { count: updatedCount }))

        const dataResponse = await fetch(email_accounts_path({ format: "json" }), {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        })
        if (dataResponse.ok) {
          const json = await dataResponse.json()
          setData(json)
        }
      } else {
        const error = await response.json()
        const errorMessage = error.error || i18n.t("js.email_accounts_center.bulk.edit_error")
        setBulkEditError(errorMessage)
        App.displayFlash("error", errorMessage)
      }
    } catch {
      const errorMessage = i18n.t("js.email_accounts_center.bulk.edit_error")
      setBulkEditError(errorMessage)
      App.displayFlash("error", errorMessage)
    } finally {
      setBulkEditLoading(false)
    }
  }, [bulkEditFlags, bulkEditData, clearSelection, setData])

  return {
    bulkEditOpen,
    setBulkEditOpen,
    bulkEditLoading,
    bulkEditError,
    bulkEditData,
    setBulkEditData,
    bulkEditAccount,
    handleEdit,
    handleBulkEditSubmit,
    getNumericIds,
  }
}

export { isPendingAccount }
export type { BulkEditData }
